<template>
  <main class="staff-page text-changes-page">
    <header class="page-heading">
      <div>
        <div class="page-eyebrow">{{ product }}</div>
        <h1>Изменения параметров</h1>
      </div>
    </header>
    <div v-if="!isLoaded" class="ui-empty">
      <Spinner />
      <span>Загрузка изменений…</span>
    </div>
    <div class="space-y-3">
      <details
        v-for="groupChange in groupedChanges"
        :key="groupChange.name"
        class="file-group ui-panel"
      >
        <summary role="button">
          <FileCode2 :size="17" />
          <span>{{ groupChange.name }}</span>
          <Badge variant="outline">{{ groupChange.changes.length }}</Badge>
        </summary>
        <div class="file-changes">
          <details
            v-for="change in groupChange.changes"
            :key="change.dateUpdated"
            class="file-change"
          >
            <summary role="button">
              <span>
                {{ formatDateTime(change.dateUpdated) }} ({{ change.new.length }} symbols)
              </span>
            </summary>
            <div class="file-change-content">
              <Tabs v-model="sometab">
                <TabsList>
                  <TabsTrigger value="content">Текущее содержимое</TabsTrigger>
                  <TabsTrigger value="diff">История изменений</TabsTrigger>
                </TabsList>
                <TabsContent value="content">
                  <pre
                    class="text-file-content"
                    tabindex="0"
                    aria-label="Содержимое файла"
                  ><code class="xml">{{ change.new }}</code></pre>
                </TabsContent>
                <TabsContent value="diff">
                  <keep-alive>
                    <component
                      :is="diffComponent"
                      v-bind="{
                        oldString: change.prev,
                        newString: change.new,
                        context: 10,
                      }"
                    />
                  </keep-alive>
                </TabsContent>
              </Tabs>
            </div>
          </details>
        </div>
      </details>
    </div>
    <p v-if="isLoaded && !groupedChanges.length" class="ui-empty">Нет изменений параметров</p>
  </main>
</template>

<script setup lang="ts">
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FileCode2 } from '@lucide/vue';

import { ref, onMounted, getCurrentInstance } from 'vue';
import axios from 'axios';
import { useRoute } from 'vue-router';
import CodeDiffWrapper from '../components/CodeDiffWrapper.vue';

const route = useRoute();
const instance = getCurrentInstance();

interface ChangeComponentWrapper {
  isLoading: boolean;
  dateUpdated: any;
  size: number;
  new: string;
  prev: string;
}

const groupedChanges = ref<
  {
    name: string;
    changes: ChangeComponentWrapper[];
  }[]
>([]);

const product = ref<string | any>(null);
const isLoaded = ref(false);
const diffComponent = CodeDiffWrapper;
const sometab = ref('content');

function formatDateTime(dateTime: string): string {
  return (
    instance?.appContext.config.globalProperties.$filters.dateTimeToDateWithTimeString(dateTime) ||
    ''
  );
}

async function initTextChanges() {
  product.value = route.params.product;
  const products = (await axios.get('/api/products/')).data as PagedList<Product>;
  const productId = products.results.find((p) => p.name == route.params.product)?.id;
  if (productId == null) {
    isLoaded.value = true;
    return;
  }

  const textInfoChanges = (
    (await axios.get('/api/properties/' + route.params.propertyId + '/text-infos/'))
      .data as TextInfoChange[]
  ).filter((t) => t.product == productId);

  const grouped = textInfoChanges.reduce((g: any, change: TextInfoChange) => {
    g[change.name] = g[change.name] || [];
    g[change.name].push(change);
    return g;
  }, {});

  for (const i in grouped) {
    const sorted = (grouped[i] as TextInfoChange[]).sort((x, y) =>
      x.updated_at > y.updated_at ? 1 : -1
    );

    const changes = [] as ChangeComponentWrapper[];

    for (let j = 0; j < sorted.length; j++) {
      const newTextChange = {
        isLoading: false,
        dateUpdated: sorted[j].updated_at,
        size: sorted[j].content.length,
        new: sorted[j].content,
        prev: j == 0 ? '' : sorted[j - 1].content,
      };
      changes.push(newTextChange);
    }

    const item = {
      name: i,
      changes: changes.sort((x, y) => (x.dateUpdated < y.dateUpdated ? 1 : -1)),
    };

    groupedChanges.value.push(item);
  }

  isLoaded.value = true;
}

onMounted(async () => {
  await initTextChanges();
});
</script>
